import {
	HttpClient,
	HttpErrorResponse,
	HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom, map, switchMap, take } from 'rxjs';

import { AlertPosition } from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import { AlertService } from '@valk-nx/components/ui-alert/src/lib/services/alert.service';
import { CheckedOption } from '@valk-nx/core/lib/interfaces/form.interface';

export const FORM_SERVICE_URL = new InjectionToken<string>(
	'form-subscribe-service-url',
);

export const FORM_API_COOKIES = new InjectionToken<string[]>(
	'form_api-cookies',
);

export type Recipients = 'main' | 'bookings' | 'business' | 'support';

@Injectable({ providedIn: 'root' })
export class FormService {
	private readonly translate = inject(TranslateService);
	private readonly http = inject(HttpClient);
	private readonly alertService = inject(AlertService);
	private readonly recaptchaV3Service = inject(ReCaptchaV3Service);
	private readonly serviceUrl = inject(FORM_SERVICE_URL);
	private readonly apiCookies = inject<{
		withCredentials: boolean;
	}>(FORM_API_COOKIES);

	submitCustomForm(
		subject: string,
		params: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
		recipient: Recipients,
		hotelSlug: string,
		emailRecipients: string[],
		recaptchaToken?: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		const headers = new HttpHeaders({
			'Accept-Language': this.translate.getCurrentLang(),
		});
		const defaultLanguage = this.translate.getCurrentLang();
		const filteredParams = params.filter(
			(param) => typeof param.value !== 'boolean',
		);
		// NOTE: if a token is provided through params, this means V2 of recaptcha is used
		if (recaptchaToken) {
			return this.recaptchaV2Submit(
				subject,
				filteredParams,
				recipient,
				hotelSlug,
				emailRecipients,
				defaultLanguage,
				headers,
				recaptchaToken,
			);
		}
		return this.recaptchaV3Submit(
			subject,
			filteredParams,
			recipient,
			hotelSlug,
			emailRecipients,
			defaultLanguage,
			headers,
		);
	}

	private recaptchaV3Submit(
		subject: string,
		params: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
		recipient: Recipients,
		hotelSlug: string,
		emailRecipients: string[],
		defaultLanguage: string,
		headers: HttpHeaders,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		return firstValueFrom(
			this.recaptchaV3Service.execute('customForm').pipe(
				switchMap((token) => {
					return this.http.post<{ data: { success: true } }>(
						`${this.serviceUrl}form/submit`,
						{
							subject,
							params,
							token,
							type: recipient,
							hotelSlug,
							emailRecipients,
							defaultLanguage,
							recaptchaVersion: 'v3',
						},
						{
							...this.apiCookies,
							headers,
						},
					);
				}),
				take(1),
				map((body) => {
					return {
						success: body.data.success,
					};
				}),
			),
		).catch((err: HttpErrorResponse) => this.formError(err));
	}

	private recaptchaV2Submit(
		subject: string,
		params: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
		recipient: Recipients,
		hotelSlug: string,
		emailRecipients: string[],
		defaultLanguage: string,
		headers: HttpHeaders,
		recaptchaToken: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		return firstValueFrom(
			this.http
				.post<{ data: { success: true } }>(
					`${this.serviceUrl}form/submit`,
					{
						subject,
						params,
						token: recaptchaToken,
						type: recipient,
						hotelSlug,
						emailRecipients,
						defaultLanguage,
						recaptchaVersion: 'v2',
					},
					{
						...this.apiCookies,
						headers,
					},
				)
				.pipe(
					take(1),
					map((body) => {
						return {
							success: body.data.success,
						};
					}),
				),
		).catch((err: HttpErrorResponse) => this.formError(err));
	}

	formError(err: HttpErrorResponse): { success: boolean; status: number } {
		this.alertService.error({
			closeable: true,
			duration: 5000,
			emphasized: false,
			hasElevation: true,
			position: AlertPosition.TopCenter,
			content: this.translate.instant('global.service.error'),
		});
		return { success: false, status: err.status };
	}
}
